import { useCallback, useEffect, useMemo, useState } from "react";
import { Input, message } from "antd";
import styled, { createGlobalStyle } from "styled-components";
import {
  CopyOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  UserOutlined,
  ClockCircleOutlined,
  StarOutlined,
  ExpandAltOutlined,
} from "@ant-design/icons";
import { formatScopeAsCleanText } from "../../lib/scopeOfWorkUtils";

const { TextArea } = Input;

/**
 * Extract a clean prose description from the raw projectSummary string.
 * Strips slot-fill lines (ZIP codes, "yes/no", "that is it", etc.) that got
 * concatenated into the summary when built from raw chat text.
 */
const SLOT_FILL_LINE_RE =
  /^\s*(?:\d{5}(?:-\d{4})?|yes|no|nope|nothing|ok|okay|sure|townhome|condo|single[- ]?family|single family home|vacant|occupied|urgent|not urgent|within \d+|next \d+[-–\d\s]*(?:days?|weeks?|months?)?|that is it|that's it|nothing else|no more|all done|all good|high[- ]?end(?:\s+fixtures?)?|standard(?:\s+(?:fixtures?|finishes?))?|budget|luxury|mid[- ]?range|[a-z])\s*$/i;

function cleanProjectDescription(raw) {
  const text = String(raw || "").trim();
  if (!text) return "";

  // If the summary uses "·" as a separator (head metadata · body), strip the head
  const dotSplit = text.match(/^([^.\n]{0,160}?)\.\s+([\s\S]+)$/);
  if (dotSplit && dotSplit[1].includes("·")) {
    // head is "bedroom remodel · townhome · ZIP 63122" - body is the real description
    return cleanBodyText(dotSplit[2]);
  }

  // No metadata head - clean the entire text
  return cleanBodyText(text);
}

function cleanBodyText(text) {
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim())
    .filter((l) => l.length >= 6 && !SLOT_FILL_LINE_RE.test(l));

  if (lines.length === 0) return text.slice(0, 200);

  // Rejoin, collapsing run-on duplicates
  const unique = [];
  for (const line of lines) {
    if (!unique.some((u) => u.toLowerCase().includes(line.toLowerCase().slice(0, 40)))) {
      unique.push(line);
    }
    if (unique.length >= 3) break; // max 3 substantive lines
  }
  return unique.join(" ").replace(/\s{2,}/g, " ").trim();
}

const PROPERTY_TYPE_DISPLAY = {
  condo: "Condo",
  townhome: "Townhome",
  "single-family": "Single-family home",
  "multi-family": "Multi-family",
};

const OCCUPANCY_DISPLAY = {
  occupied: "Owner-occupied during work",
  vacant: "Vacant",
  rental: "Rental",
};

const FINISHES_DISPLAY = {
  basic: "Standard",
  standard: "Standard",
  budget: "Budget",
  "mid-range": "Mid-range",
  "high-end": "High-end",
  luxury: "Luxury",
};

const TIMELINE_DISPLAY = {
  emergency: "Urgent",
  planned: "Within 30 days",
  researching: "Flexible timing",
  flexible_planning: "Flexible timing",
};

/** Title-case a raw intake token (e.g. "mid-range" → "Mid-range"). */
function titleCaseToken(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  return s
    .split(/(\s+|[-/])/)
    .map((part) => {
      if (!part || /^[\s-/]+$/.test(part)) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join("");
}

function formatPropertyTypeDisplay(raw) {
  if (!raw) return null;
  const key = String(raw).toLowerCase().trim();
  return PROPERTY_TYPE_DISPLAY[key] || titleCaseToken(raw);
}

function formatOccupancyDisplay(raw) {
  if (!raw) return null;
  const key = String(raw).toLowerCase().trim();
  return OCCUPANCY_DISPLAY[key] || titleCaseToken(raw);
}

function formatFinishesDisplay(raw) {
  if (!raw) return null;
  const key = String(raw).toLowerCase().trim();
  return FINISHES_DISPLAY[key] || titleCaseToken(raw);
}

function formatTimelineDisplay(raw) {
  if (!raw) return null;
  const key = String(raw).toLowerCase().trim();
  return TIMELINE_DISPLAY[key] || titleCaseToken(raw);
}

/**
 * Build structured detail rows from scope metadata fields.
 * Falls back to parsing from projectSummary text if a field isn't set.
 */
function displayHomeownerZip(raw) {
  if (!raw) return null;
  const m = String(raw).match(/\b(\d{5})\b/);
  return m ? m[1] : String(raw).trim();
}

function buildDetailRows(scope) {
  const text = String(scope?.projectSummary || "").toLowerCase();
  const rows = [];

  // ZIP / location — always show full 5-digit ZIP for the homeowner (never vendor-masked strings)
  const zip =
    displayHomeownerZip(scope?.zipCode) ||
    text.match(/\b(\d{5})\b/)?.[1];
  if (zip) rows.push({ icon: "location", label: "Location", value: String(zip) });

  // Project size (small / medium / large from surgical intake or fallback parsing)
  const rawSize = scope?.size;
  const sizeLabel = rawSize
    ? rawSize.charAt(0).toUpperCase() + rawSize.slice(1)
    : /\blarge\b/i.test(text)
      ? "Large"
      : /\bmedium\b/i.test(text)
        ? "Medium"
        : /\bsmall\b/i.test(text)
          ? "Small"
          : null;
  if (sizeLabel) rows.push({ icon: "size", label: "Project size", value: sizeLabel });

  // Property type
  const propType =
    formatPropertyTypeDisplay(scope?.propertyType) ||
    (/town[- ]?home/i.test(text) ? "Townhome" :
     /condo/i.test(text) ? "Condo" :
     /single[- ]?family/i.test(text) ? "Single-family home" :
     /multi[- ]?family/i.test(text) ? "Multi-family" : null);
  if (propType) rows.push({ icon: "home", label: "Property", value: propType });

  // Occupancy
  const occ =
    formatOccupancyDisplay(scope?.occupancy) ||
    (/living (there|here)|occupied during/i.test(text) ? "Owner-occupied during work" :
     /\bvacant\b/i.test(text) ? "Vacant" :
     /\brental?\b/i.test(text) ? "Rental" : null);
  if (occ) rows.push({ icon: "user", label: "Occupancy", value: occ });

  // Timeline - map backend enum values to human-readable display labels before
  // falling back to regex parsing of the project summary text.
  const rawTl = scope?.timeline;
  const tl =
    formatTimelineDisplay(rawTl) ||
    // Fallback: parse projectSummary text. Flexible / negations before bare "urgent".
    (/\b(whenever|flexible\s+on\s+timing|flexible)\b/i.test(text)
      ? "Flexible timing"
      : /\b(not\s+urgent|no\s+rush|no\s+hurry|can\s+wait)\b/i.test(text)
      ? "Not urgent"
      : /\burgent\b/i.test(text)
        ? "Urgent"
        : (() => {
            const m = text.match(/\b(?:next\s+)?(\d+)[-–](\d+)\s*(?:days?|fays?)\b/);
            return m ? `${m[1]}–${m[2]} days` : null;
          })() ||
          (() => {
            const m = text.match(/\b(?:within|next)\s+(\d+)\s*(?:days?|fays?|[a-z]{1,5})\b/i);
            return m ? `Within ${m[1]} days` : null;
          })() ||
          null);
  if (tl) rows.push({ icon: "clock", label: "Timeline", value: tl });

  // Finishes
  const fin =
    formatFinishesDisplay(scope?.finishes) ||
    (/high[- ]?end/i.test(text) ? "High-end" :
     /luxury/i.test(text) ? "Luxury" :
     /mid[- ]?range/i.test(text) ? "Mid-range" :
     /standard/i.test(text) ? "Standard" : null);
  if (fin) rows.push({ icon: "star", label: "Finishes", value: fin });

  return rows;
}

const DETAIL_ICONS = {
  location: EnvironmentOutlined,
  size: ExpandAltOutlined,
  home: HomeOutlined,
  user: UserOutlined,
  clock: ClockCircleOutlined,
  star: StarOutlined,
};

/**
 * Editable Scope of Work card for the chat bids panel.
 */
export default function EditableScopeCard({
  scope,
  onScopeChange,
  compact = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(scope);

  useEffect(() => {
    setDraft(scope);
    setEditing(false);
  }, [scope]);

  const display = draft || scope;
  const hasScope = Boolean(display?.projectSummary?.trim());

  const cleanDescription = useMemo(
    () => cleanProjectDescription(display?.projectSummary),
    [display?.projectSummary]
  );

  const detailRows = useMemo(() => buildDetailRows(display), [display]);

  const handleField = useCallback(
    (path, value) => {
      setDraft((prev) => {
        const base = prev || scope;
        if (!base) return prev;
        if (path === "projectSummary") return { ...base, projectSummary: value };
        if (path === "summaryLine") return { ...base, estimatedCost: { ...base.estimatedCost, summaryLine: value } };
        if (path === "notes") return { ...base, estimatedCost: { ...base.estimatedCost, notes: value } };
        return prev;
      });
    },
    [scope]
  );

  const handleTaskChange = useCallback(
    (taskId, field, value) => {
      setDraft((prev) => {
        const base = prev || scope;
        if (!base) return prev;
        return {
          ...base,
          detailedTasks: (base.detailedTasks || []).map((t) =>
            t.id === taskId ? { ...t, [field]: value } : t
          ),
        };
      });
    },
    [scope]
  );

  const cancelEdits = useCallback(() => {
    setDraft(scope);
    setEditing(false);
  }, [scope]);

  const saveEdits = useCallback(() => {
    if (!draft) return;
    onScopeChange?.(draft);
    setEditing(false);
    message.success("Scope saved");
  }, [draft, onScopeChange]);

  const copyCleanText = useCallback(async () => {
    const text = formatScopeAsCleanText(display);
    if (!text) {
      message.warning("Nothing to copy yet. Keep chatting to build scope.");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      message.success("Scope copied to clipboard");
    } catch {
      message.error("Could not copy. Try selecting the text manually.");
    }
  }, [display]);

  if (!hasScope) {
    return (
      <>
        <ScopeFieldStyles />
        <ScopeCard $compact={compact}>
          <EmptyCard>
            <EmptyTitle>Building your scope</EmptyTitle>
            <ScopeEmpty>
              Keep chatting about location, what you want done, and timing.
              Clumse will assemble an itemized scope here for you to review
              before vendor search.
            </ScopeEmpty>
          </EmptyCard>
        </ScopeCard>
      </>
    );
  }

  return (
    <>
      <ScopeFieldStyles />
      <ScopeCard $compact={compact}>
        <Toolbar>
          {editing ? (
            <>
              <ToolbarBtn type="button" $primary onClick={saveEdits}>
                <CheckOutlined /> Save changes
              </ToolbarBtn>
              <ToolbarBtn type="button" onClick={cancelEdits}>
                <CloseOutlined /> Cancel
              </ToolbarBtn>
            </>
          ) : (
            <>
              <ToolbarBtn type="button" onClick={() => setEditing(true)}>
                <EditOutlined /> Edit
              </ToolbarBtn>
              <ToolbarBtn type="button" onClick={() => void copyCleanText()}>
                <CopyOutlined /> Copy
              </ToolbarBtn>
            </>
          )}
        </Toolbar>

        {/* ── Project Summary ─────────────────────────────── */}
        <SectionCard>
          <SectionLabel>Project summary</SectionLabel>
          {editing ? (
            <TextArea
              className="scope-sow-field"
              value={display.projectSummary}
              onChange={(e) => handleField("projectSummary", e.target.value)}
              autoSize={{ minRows: 4, maxRows: 10 }}
            />
          ) : (
            <ScopeDescription>
              {cleanDescription || display.projectSummary}
            </ScopeDescription>
          )}

          {display.materialsPlan ? (
            <MaterialsRow>
              <MaterialsLabel>Materials</MaterialsLabel>
              <span>{display.materialsPlan}</span>
            </MaterialsRow>
          ) : null}
        </SectionCard>

        {/* ── Project Details ─────────────────────────────── */}
        {detailRows.length > 0 && !editing ? (
          <SectionCard>
            <SectionLabel>Project details</SectionLabel>
            <DetailGrid>
              {detailRows.map((row) => {
                const Icon = DETAIL_ICONS[row.icon];
                return (
                  <DetailRow key={row.label}>
                    <DetailIcon>{Icon ? <Icon /> : null}</DetailIcon>
                    <DetailKey>{row.label}</DetailKey>
                    <DetailValue>{row.value}</DetailValue>
                  </DetailRow>
                );
              })}
            </DetailGrid>
          </SectionCard>
        ) : null}

        {/* ── Estimated Parts & Labor ─────────────────────── */}
        <SectionCard>
          <SectionLabel>Estimated parts &amp; labor</SectionLabel>
          {editing ? (
            <>
              <TextArea
                className="scope-sow-field"
                value={display.estimatedCost?.summaryLine || ""}
                onChange={(e) => handleField("summaryLine", e.target.value)}
                autoSize={{ minRows: 2, maxRows: 4 }}
                placeholder="Parts $X–$Y · Labor $A–$B"
              />
              <TextArea
                className="scope-sow-field"
                style={{ marginTop: 8 }}
                value={display.estimatedCost?.notes || ""}
                onChange={(e) => handleField("notes", e.target.value)}
                autoSize={{ minRows: 2, maxRows: 3 }}
                placeholder="Optional notes for contractors"
              />
            </>
          ) : (
            <EstimateBox>
              <ScopeBody>{display.estimatedCost?.summaryLine}</ScopeBody>
              {display.estimatedCost?.notes ? (
                <EstimateNote>{display.estimatedCost.notes}</EstimateNote>
              ) : null}
            </EstimateBox>
          )}
        </SectionCard>

        {/* ── Work Outline ────────────────────────────────── */}
        {(display.detailedTasks || []).length > 0 ? (
          <SectionCard>
            <SectionLabel>Work outline</SectionLabel>
            <TaskList>
              {(display.detailedTasks || []).map((task, idx) => (
                <TaskRow key={task.id || idx}>
                  <TaskNum aria-hidden>{idx + 1}</TaskNum>
                  <TaskBody>
                    {editing ? (
                      <>
                        <Input
                          className="scope-sow-field"
                          value={task.label}
                          onChange={(e) =>
                            handleTaskChange(task.id, "label", e.target.value)
                          }
                          placeholder="Task description"
                        />
                        <Input
                          className="scope-sow-field"
                          style={{ marginTop: 8 }}
                          value={task.quantity || ""}
                          onChange={(e) =>
                            handleTaskChange(task.id, "quantity", e.target.value)
                          }
                          placeholder="Qty or unit (optional)"
                        />
                      </>
                    ) : (
                      <>
                        <TaskLabel>{task.label}</TaskLabel>
                        {task.quantity ? (
                          <TaskMeta>{task.quantity}</TaskMeta>
                        ) : null}
                      </>
                    )}
                  </TaskBody>
                </TaskRow>
              ))}
            </TaskList>
          </SectionCard>
        ) : null}
      </ScopeCard>
    </>
  );
}

const ScopeFieldStyles = createGlobalStyle`
  .scope-sow-field.ant-input,
  textarea.scope-sow-field.ant-input {
    border-radius: 8px !important;
    border: 1px solid #dadce0 !important;
    background: #fff !important;
    font-size: 0.875rem !important;
    line-height: 1.5 !important;
    text-align: left !important;
    color: #1e293b !important;
    box-shadow: none !important;
  }

  .scope-sow-field.ant-input:hover,
  textarea.scope-sow-field.ant-input:hover {
    border-color: #bdc1c6 !important;
  }

  .scope-sow-field.ant-input:focus,
  textarea.scope-sow-field.ant-input:focus {
    border-color: #94a3b8 !important;
    box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.2) !important;
  }
`;

const ScopeCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: ${(p) => (p.$compact ? "4px 0 8px" : "2px 0 10px")};
  text-align: left;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 4px;
`;

const ToolbarBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  border: 1px solid
    ${(p) => (p.$primary ? "var(--color-action, #ff5722)" : "#dadce0")};
  background: ${(p) => (p.$primary ? "var(--color-action, #ff5722)" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : "#3c4043")};

  &:hover {
    background: ${(p) => (p.$primary ? "#f4511e" : "#f8fafc")};
    border-color: ${(p) => (p.$primary ? "#f4511e" : "#bdc1c6")};
    color: ${(p) => (p.$primary ? "#fff" : "#1e293b")};
  }
`;

const SectionCard = styled.section`
  padding: 14px 14px 12px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e8eaed;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.04);
  text-align: left;
`;

const SectionLabel = styled.h4`
  margin: 0 0 10px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5f6368;
`;

/** Clean prose description - no rounded bubble, no border-radius */
const ScopeDescription = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #1e293b;
  font-weight: 400;
  text-align: left;
`;

/* ── Project Details grid ────────────────────────────── */

const DetailGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 18px 80px 1fr;
  align-items: center;
  gap: 8px;
  padding: 7px 0;
  border-bottom: 1px solid #f1f3f4;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const DetailIcon = styled.span`
  color: #9ca3af;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DetailKey = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
`;

const DetailValue = styled.span`
  font-size: 0.8125rem;
  color: #1e293b;
  line-height: 1.45;
`;

/* ── Misc shared ─────────────────────────────────────── */

const ScopeBody = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: #1e293b;
  white-space: pre-wrap;
  text-align: left;
`;

const MaterialsRow = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f3f4;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #3c4043;
  text-align: left;
`;

const MaterialsLabel = styled.span`
  display: block;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #80868b;
  margin-bottom: 4px;
`;

const EstimateBox = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e8eaed;
  text-align: left;
`;

const EstimateNote = styled.p`
  margin: 8px 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: #64748b;
`;

const EmptyCard = styled.div`
  padding: 20px 16px;
  border-radius: 12px;
  background: #fff;
  border: 1px dashed #dadce0;
  text-align: left;
`;

const EmptyTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
`;

const ScopeEmpty = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.55;
  color: #64748b;
`;

const TaskList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TaskRow = styled.li`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f4;
  text-align: left;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const TaskNum = styled.span`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
`;

const TaskBody = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const TaskLabel = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
  color: #1e293b;
`;

const TaskMeta = styled.div`
  font-size: 0.78rem;
  color: #64748b;
  margin-top: 4px;
`;
